import { Field } from './ui';
import { Icon } from './Icon';

// "Is everything there?" for a single-product lot. An incomplete item stays listed as its product
// only while MORE than half of it is there; half or less goes into a mixed/parts lot.

export interface CompletenessDraft {
  completeness: 'complete' | 'incomplete';
  piecesPresent: string;
  piecesTotal: string;
  overHalfConfirmed: boolean;
  missingParts: string;
}

export const COMPLETE: CompletenessDraft = { completeness: 'complete', piecesPresent: '', piecesTotal: '', overHalfConfirmed: false, missingParts: '' };

export const completenessFromLot = (l: { completeness: 'complete' | 'incomplete'; piecesPresent: number | null; piecesTotal: number | null; overHalfConfirmed: boolean; missingParts: string | null }): CompletenessDraft => ({
  completeness: l.completeness,
  piecesPresent: l.piecesPresent?.toString() ?? '',
  piecesTotal: l.piecesTotal?.toString() ?? '',
  overHalfConfirmed: l.overHalfConfirmed,
  missingParts: l.missingParts ?? '',
});

const count = (s: string) => (/^\d+$/.test(s.trim()) ? Number(s.trim()) : null);

/** Why this can't be saved as the product yet (null = fine). `underHalf` means it belongs in a mixed lot. */
export function completenessProblem(d: CompletenessDraft): { message: string; underHalf?: boolean } | null {
  if (d.completeness === 'complete') return null;
  const [have, full] = [count(d.piecesPresent), count(d.piecesTotal)];
  const typed = d.piecesPresent.trim() !== '' || d.piecesTotal.trim() !== '';
  if (typed) {
    if (have === null || full === null) return { message: 'Enter both numbers: pieces here and pieces in the full item.' };
    if (have < 1 || have >= full) return { message: 'Pieces here must be at least 1 and fewer than the full item.' };
    if (have * 2 <= full) return { message: `${have} of ${full}: that's half or less. This can't be listed as this product.`, underHalf: true };
  } else if (!d.overHalfConfirmed) {
    return { message: 'Count the pieces, or tick "more than half of the item is here".' };
  }
  if (!d.missingParts.trim()) return { message: "Say what's missing." };
  return null;
}

/** Fields for the API (create or edit). */
export function completenessBody(d: CompletenessDraft) {
  if (d.completeness === 'complete') return { completeness: 'complete' as const };
  const [have, full] = [count(d.piecesPresent), count(d.piecesTotal)];
  return {
    completeness: 'incomplete' as const,
    piecesPresent: have,
    piecesTotal: full,
    overHalfConfirmed: have === null && d.overHalfConfirmed,
    missingParts: d.missingParts.trim() || null,
  };
}

export function CompletenessFields({ value, onChange, onUseMixed }: { value: CompletenessDraft; onChange: (d: CompletenessDraft) => void; onUseMixed?: () => void }) {
  const set = (patch: Partial<CompletenessDraft>) => onChange({ ...value, ...patch });
  const problem = completenessProblem(value);
  const counted = value.piecesPresent.trim() !== '' || value.piecesTotal.trim() !== '';
  return (
    <div>
      <div className="field-label">Is everything there?</div>
      <div className="seg">
        <button type="button" className={`btn btn-small ${value.completeness === 'complete' ? 'on' : ''}`} onClick={() => set({ completeness: 'complete' })}>
          Yes, complete
        </button>
        <button type="button" className={`btn btn-small ${value.completeness === 'incomplete' ? 'on' : ''}`} onClick={() => set({ completeness: 'incomplete' })}>
          No, pieces missing
        </button>
      </div>
      {value.completeness === 'incomplete' && (
        <>
          <div className="grid2">
            <Field label="Pieces here">
              <input value={value.piecesPresent} onChange={(e) => set({ piecesPresent: e.target.value })} inputMode="numeric" placeholder="e.g. 8" />
            </Field>
            <Field label="Pieces in the full item">
              <input value={value.piecesTotal} onChange={(e) => set({ piecesTotal: e.target.value })} inputMode="numeric" placeholder="e.g. 11" />
            </Field>
          </div>
          {!counted && (
            <label className="check">
              <input type="checkbox" checked={value.overHalfConfirmed} onChange={(e) => set({ overHalfConfirmed: e.target.checked })} /> Counting doesn't fit this item: more than half
              of it is here
            </label>
          )}
          <Field label="What's missing (required)">
            <textarea value={value.missingParts} onChange={(e) => set({ missingParts: e.target.value })} rows={2} maxLength={2000} placeholder="e.g. 3 small containers and 2 lids" />
          </Field>
          <div className={`half-check small ${problem ? '' : 'ok'}`}>
            {problem ? (
              <span className={problem.underHalf ? 'field-error' : 'muted'}><Icon name="x" size={15} /> {problem.message}</span>
            ) : (
              <><Icon name="check" size={15} className="ok-text" /> {counted ? `${value.piecesPresent} of ${value.piecesTotal}: ` : ''}stays listed under this product</>
            )}
          </div>
          {problem?.underHalf && onUseMixed && (
            <button type="button" className="btn btn-small" onClick={onUseMixed}>
              List as a mixed / parts lot instead
            </button>
          )}
        </>
      )}
    </div>
  );
}
